import React, { useState } from "react"
import { useMutation } from "@apollo/client"
import { Popover } from "reactstrap"

import { UpdateIncidentDocumentMutation } from "./queries"

/**
 * Shared chrome for the small edits made from the Details tab.
 *
 * Kept to one small card -- the field label doubles as the title, and Cancel closes it,
 * so there is no header band and no separate close button. reactstrap's
 * PopoverHeader/PopoverBody are avoided for the same reason: their Bootstrap type and
 * spacing are much heavier than the rest of this modal.
 */
const EditPopover = ({ open, target, label, inputId, hint, error, saving, canSave, onClose, onSave, children }) => (
    <Popover className="idm-popover" isOpen={open} target={target} toggle={onClose} placement="left" trigger="legacy">
        <div className="idm-popover-card">
            <label className="idm-popover-label" htmlFor={inputId}>{label}</label>
            {!!error && <div className="idm-popover-error">{error}</div>}
            {children}
            {!!hint && <p className="idm-popover-hint">{hint}</p>}
            <div className="idm-popover-foot">
                <button type="button" className="idm-popover-btn" onClick={onClose}>Cancel</button>
                <button
                    type="button"
                    className="idm-popover-btn idm-popover-btn-primary"
                    disabled={!canSave || saving}
                    onClick={onSave}
                >
                    {saving ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    </Popover>
)

/**
 * One mutation backs every field edited from here, so the call and its error handling
 * live in one place rather than being repeated per field.
 */
export function useDocumentFieldSave(documentId, onSaved, onClose) {
    const [error, setError] = useState(null)
    const [updateDocument, { loading: saving }] = useMutation(UpdateIncidentDocumentMutation)

    const save = async (fields, fallbackMessage) => {
        setError(null)
        try {
            const { data } = await updateDocument({
                variables: { input: { incidentDocumentId: documentId, ...fields } },
            })
            const payload = data && data.updateIncidentDocument
            if (!payload || !payload.ok) {
                const message = payload && payload.errors && payload.errors[0] && payload.errors[0].messages[0]
                setError(message || fallbackMessage)
                return
            }
            if (onSaved) onSaved()
            if (onClose) onClose()
        } catch (e) {
            setError(e.message)
        }
    }

    return { save, saving, error, setError }
}

export default EditPopover
